import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, CheckCircle, MessageSquare, Calendar, Target, Star, Users, CreditCard, CalendarClock, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { coachingAPI } from '../lib/api';
import { initializeCsrf } from '../utils/csrf';
import { useWebSocketNotifications } from '../contexts/WebSocketNotificationProvider';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { lastMessage, isConnected } = useWebSocketNotifications();

  useEffect(() => {
    // Initialize CSRF token on mount
    initializeCsrf();
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  // Refresh when WebSocket receives new notification
  useEffect(() => {
    if (lastMessage?.type === 'notification' || lastMessage?.type === 'pending_notifications') {
      loadUnreadCount();
      if (isOpen) {
        loadNotifications();
      }
    }
  }, [lastMessage, isOpen]);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  // Refresh unread count when dropdown closes (in case user marked some as read)
  useEffect(() => {
    if (!isOpen) {
      loadUnreadCount();
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const response = await coachingAPI.getUnreadNotificationCount();
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await coachingAPI.getNotifications();
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      // Ensure CSRF is initialized before making the call
      await initializeCsrf();
      await coachingAPI.markNotificationRead(notificationId);
      // Update local state immediately
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      // Decrease unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      return true;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return false;
    }
  };

  const markAllAsRead = async () => {
    try {
      await initializeCsrf();
      await coachingAPI.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Only mark as read if it's currently unread
    if (!notification.read) {
      // Update state immediately for instant UI feedback
      setNotifications(prev => prev.map(n => 
        n.id === notification.id ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Then make the API call (fire and forget for faster UX)
      markAsRead(notification.id);
    }
    
    // Navigate to the appropriate page
    const link = getNavigationLink(notification);
    if (link) {
      navigate(link);
      setIsOpen(false);
    }
  };

  // Map notification types and links to proper navigation routes
  const getNavigationLink = (notification) => {
    const { type, link } = notification;
    
    // If there's a direct link, validate and map it
    if (link) {
      // Map old/invalid links to proper routes
      if (link.startsWith('/messages/')) {
        return '/messages';
      }
      if (link.startsWith('/suggestions/')) {
        return '/my-coach';
      }
      if (link.startsWith('/goals/')) {
        return '/my-coach';
      }
      if (link === '/sessions') {
        return '/sessions';
      }
      // Return the link as-is if it seems valid
      return link;
    }
    
    // Map by notification type if no link
    switch (type) {
      case 'message':
        return '/messages';
      case 'session':
      case 'session_scheduled':
      case 'session_cancelled':
      case 'session_reminder':
        return '/sessions';
      case 'event_reminder':
        return '/my-events';
      case 'suggestion':
      case 'goal':
      case 'review':
        return '/my-coach';
      case 'connection':
      case 'connection_request':
      case 'connection_accepted':
      case 'connection_declined':
      case 'coach_request':
      case 'coach_request_accepted':
      case 'coach_request_declined':
        return '/my-coach';
      case 'org_invite':
      case 'org_invite_accepted':
        return '/my-organization';
      case 'subscription':
      case 'payment':
      case 'upgrade':
        return '/subscription-management';
      default:
        return '/notifications';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message':
        return <MessageSquare className="w-5 h-5 text-blue-500" />;
      case 'session':
        return <Calendar className="w-5 h-5 text-green-500" />;
      case 'event_reminder':
        return <CalendarClock className="w-5 h-5 text-purple-500" />;
      case 'suggestion':
        return <CheckCircle className="w-5 h-5 text-purple-500" />;
      case 'goal':
        return <Target className="w-5 h-5 text-orange-500" />;
      case 'review':
        return <Star className="w-5 h-5 text-yellow-500" />;
      case 'connection':
        return <Users className="w-5 h-5 text-indigo-500" />;
      case 'org_invite':
      case 'org_invite_accepted':
        return <Users className="w-5 h-5 text-sky-500" />;
      case 'subscription':
      case 'payment':
        return <CreditCard className="w-5 h-5 text-emerald-500" />;
      case 'upgrade':
        return <Gift className="w-5 h-5 text-amber-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="relative z-[9998]" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        data-testid="notification-bell-button"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full" data-testid="notification-count">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {/* Live indicator when WebSocket is connected */}
        {isConnected && (
          <span className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border border-white" title="Live updates enabled" />
        )}
      </button>

      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-2xl border border-gray-200"
          style={{ zIndex: 99999, minHeight: '100px' }}
          data-testid="notification-dropdown"
        >
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                {isConnected && (
                  <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                    Live
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification.read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {notification.title}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(notification.created_at).toLocaleString()}
                        </p>
                      </div>
                      {!notification.read && (
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  navigate('/notifications');
                  setIsOpen(false);
                }}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
